'use client';
import { useMemo, useState } from 'react';
import { IconInfo } from '@/assets/icons';
import CustomDatePicker from '@/components/ui/CustomDatePicker';
import PhoneNumberInput from '@/components/ui/PhoneNumberInput';
import Select from '@/components/ui/Select';
import { splitPhoneNumber } from '@/utils/phoneParse';
import AddressSearchInput from '@/components/ui/AddressSearchInput';
import Checkbox from '@/components/ui/Checkbox';
import FileInput from '@/components/ui/FileInput';
import StreetSearchInput from '@/components/ui/StreetSearchInput';
import BuildingSearchInput from '@/components/ui/BuildingSearchInput';
import RoomSearchInput from '@/components/ui/RoomSearchInput';
import { useLanguage } from '@/context/LanguageContext';
import {
  getYesNoOptions,
  getConstructionCarBuildingTypeOptions,
  getConstructionMaterialOptions,
  getConstructionCarWorkDurationOptions,
  getCarPolicyEndDateYmd,
} from '@/lib/calculatorI18n';
import { isCompanyCodeFromValue } from './constants';
import styles from '../Step1BasicData.module.css';
import AccountEmailInput from '@/components/calculator/AccountEmailInput';

export default function ConstructionFormFields({
  formData,
  setFormData,
  validationErrors,
  showValidationErrors,
  handlePolicyholderPersonalCodeChange,
  handlePolicyholderAddressChange,
  handleConstructionAddressChange,
  handleBuildingTypeChange,
  handleConstructionMaterialChange,
  handleWorkValueChange,
  handleBuildingsCountChange,
  handleBooleanChange,
  handleFileChange,
  handleConstructionAddressPatch,
  handleConstructionSettlementSelect,
  setValidationErrors,
  hidePolicyholder = false,
}) {
  const { t } = useLanguage();
  const yesNoOptions = useMemo(() => getYesNoOptions(t), [t]);
  const constructionCarBuildingOpts = useMemo(
    () => getConstructionCarBuildingTypeOptions(t),
    [t]
  );
  const constructionMaterialOpts = useMemo(
    () => getConstructionMaterialOptions(t),
    [t]
  );

  const workDurationOptions = useMemo(
    () => getConstructionCarWorkDurationOptions(t),
    [t]
  );

  const isCompanyPolicyholder = isCompanyCodeFromValue(
    formData.policyholder?.personal_code
  );

  /** Past work starts + reasonable future window for CustomDatePicker year range */
  const constructionWorkBounds = useMemo(() => {
    const y = new Date().getFullYear();
    const min = new Date(y - 50, 0, 1);
    min.setHours(0, 0, 0, 0);
    const max = new Date(y + 10, 11, 31);
    max.setHours(23, 59, 59, 999);
    return { min, max };
  }, []);

  /** Which construction CAR info tooltip is visible (hover/focus on IconInfo) */
  const [activeConstructionInfo, setActiveConstructionInfo] = useState(null);

  const { countryCode: phoneCountryCode, phone: phoneNational } = useMemo(
    () => splitPhoneNumber(formData.policyholder?.phone_number),
    [formData.policyholder?.phone_number]
  );

  const updatePolicyholderPhone = (countryCode, nationalDigits) => {
    const codeDigits = (countryCode || '+370').replace(/\D/g, '');
    let digits = String(nationalDigits || '').replace(/\D/g, '').slice(0, 8);
    if (digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
      digits = digits.slice(codeDigits.length).slice(0, 8);
    }
    const phone_number = digits ? `+${codeDigits}${digits}` : '';
    setFormData(prev => ({
      ...prev,
      policyholder: {
        ...prev.policyholder,
        phone_number,
      },
    }));
  };

  return (
    <>
      {!hidePolicyholder ? (
        <>
      {/* Policyholder Section */}
      <div
        className={styles.formGroup}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <div
          className={styles.labelWithInfo}
          style={{ marginBottom: '20px' }}
        >
          <span
            className={styles.label}
            style={{ fontSize: '18px', fontWeight: '500', marginBottom: 0 }}
          >
            {t('calculator.forms.policyholder_section')}
          </span>
          <div
            className={styles.fieldInfoIconWrap}
            onMouseEnter={() => setActiveConstructionInfo('policyholder')}
            onMouseLeave={() => setActiveConstructionInfo(null)}
          >
            <button
              type='button'
              className={styles.packageInfoButton}
              aria-label={t('calculator.forms.policyholder_section_info')}
              onFocus={() => setActiveConstructionInfo('policyholder')}
              onBlur={() => setActiveConstructionInfo(null)}
            >
              <IconInfo width={18} height={18} color='currentColor' />
            </button>
            {activeConstructionInfo === 'policyholder' && (
              <div
                className={`${styles.fieldInfoTooltip} ${styles.fieldInfoTooltipWide}`}
                role='tooltip'
              >
                {t('calculator.forms.policyholder_section_info')}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.code_label')}</label>
        <input
          className={`${styles.input} ${validationErrors.policyholder_personal_code ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.code_placeholder')}
          value={formData.policyholder?.personal_code || ''}
          autoComplete='username'
          onChange={handlePolicyholderPersonalCodeChange}
          maxLength={11}
          inputMode='numeric'
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_personal_code ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_personal_code || ''}
        </div>
      </div>

      {isCompanyPolicyholder ? (
        <div className={styles.formGroup}>
          <label className={styles.label}>
            {t('calculator.forms.company_name')}
          </label>
          <input
            className={`${styles.input} ${validationErrors.policyholder_company_name ? styles.inputError : ''}`}
            type='text'
            placeholder={t('calculator.forms.company_name_placeholder')}
            value={formData.policyholder?.company_name || ''}
            autoComplete='organization'
            onChange={e => {
              const value = e.target.value;
              setFormData(prev => ({
                ...prev,
                policyholder: {
                  ...prev.policyholder,
                  company_name: value,
                },
              }));
              if (value.trim() && setValidationErrors) {
                setValidationErrors(prev => {
                  if (!prev?.policyholder_company_name) return prev;
                  const next = { ...prev };
                  delete next.policyholder_company_name;
                  return next;
                });
              }
            }}
            maxLength={64}
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_company_name ? styles.errorHighlight : ''}`}
          >
            {validationErrors.policyholder_company_name || ''}
          </div>
        </div>
      ) : (
        <>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('calculator.forms.first_name')}</label>
            <input
              className={`${styles.input} ${validationErrors.policyholder_first_name ? styles.inputError : ''}`}
              type='text'
              placeholder={t('calculator.forms.first_name_placeholder')}
              value={formData.policyholder?.first_name || ''}
              autoComplete='given-name'
              onChange={e => {
                setFormData(prev => ({
                  ...prev,
                  policyholder: {
                    ...prev.policyholder,
                    first_name: e.target.value,
                  },
                }));
              }}
            />
            <div
              className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_first_name ? styles.errorHighlight : ''}`}
            >
              {validationErrors.policyholder_first_name || ''}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>{t('calculator.forms.last_name')}</label>
            <input
              className={`${styles.input} ${validationErrors.policyholder_last_name ? styles.inputError : ''}`}
              type='text'
              placeholder={t('calculator.forms.last_name_placeholder')}
              value={formData.policyholder?.last_name || ''}
              autoComplete='family-name'
              onChange={e => {
                setFormData(prev => ({
                  ...prev,
                  policyholder: {
                    ...prev.policyholder,
                    last_name: e.target.value,
                  },
                }));
              }}
            />
            <div
              className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_last_name ? styles.errorHighlight : ''}`}
            >
              {validationErrors.policyholder_last_name || ''}
            </div>
          </div>
        </>
      )}

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.email')}</label>
        <AccountEmailInput
          className={`${styles.input} ${validationErrors.policyholder_email ? styles.inputError : ''}`}
          placeholder={t('calculator.forms.email_placeholder')}
          value={formData.policyholder?.email || ''}
          onChange={e => {
            setFormData(prev => ({
              ...prev,
              policyholder: {
                ...prev.policyholder,
                email: e.target.value,
              },
            }));
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_email ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_email || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.phone')}</label>
        <PhoneNumberInput
          countryCode={phoneCountryCode}
          onCountryCodeChange={code => updatePolicyholderPhone(code, phoneNational)}
          value={phoneNational}
          onChange={digits => updatePolicyholderPhone(phoneCountryCode, digits)}
          wrapperClassName={`${styles.inputWithIcon} ${validationErrors.policyholder_phone_number ? styles.inputError : ''}`}
          inputClassName={styles.input}
          placeholder={t('account.details.placeholder_phone')}
          autoComplete='tel-national'
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_phone_number ? styles.errorHighlight : ''}`}
        >
          {validationErrors.policyholder_phone_number || ''}
        </div>
      </div>

        </>
      ) : null}

      <div className={styles.formGroup}>
        <div className={styles.labelWithInfo}>
          <span className={styles.label}>
            {t('calculator.forms.work_start_date_car')}
          </span>
          <div
            className={styles.fieldInfoIconWrap}
            onMouseEnter={() => setActiveConstructionInfo('work_start')}
            onMouseLeave={() => setActiveConstructionInfo(null)}
          >
            <button
              type='button'
              className={styles.packageInfoButton}
              aria-label={t('calculator.forms.work_start_date_car_info')}
              onFocus={() => setActiveConstructionInfo('work_start')}
              onBlur={() => setActiveConstructionInfo(null)}
            >
              <IconInfo width={18} height={18} color='currentColor' />
            </button>
            {activeConstructionInfo === 'work_start' && (
              <div
                className={`${styles.fieldInfoTooltip} ${styles.fieldInfoTooltipWide}`}
                role='tooltip'
              >
                {t('calculator.forms.work_start_date_car_info')}
              </div>
            )}
          </div>
        </div>
        <CustomDatePicker
          title={t('calculator.forms.work_start_date_car')}
          value={formData.startDate || ''}
          onChange={formatted => {
            setFormData(prev => {
              const next = { ...prev, startDate: formatted };
              if (formatted && prev.work_duration_years) {
                next.endDate = getCarPolicyEndDateYmd(
                  formatted,
                  prev.work_duration_years
                );
              }
              return next;
            });
          }}
          minDate={constructionWorkBounds.min}
          maxDate={constructionWorkBounds.max}
          error={!!validationErrors.startDate}
        />
        <div
          key={`car-start-date-${(validationErrors.startDate || '').length}`}
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.startDate ? styles.errorHighlight : ''}`}
        >
          {validationErrors.startDate || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <div className={styles.labelWithInfo}>
          <span className={styles.label}>
            {t('calculator.forms.work_duration_label')}
          </span>
          <div
            className={styles.fieldInfoIconWrap}
            onMouseEnter={() => setActiveConstructionInfo('work_duration')}
            onMouseLeave={() => setActiveConstructionInfo(null)}
          >
            <button
              type='button'
              className={styles.packageInfoButton}
              aria-label={t('calculator.forms.work_duration_info')}
              onFocus={() => setActiveConstructionInfo('work_duration')}
              onBlur={() => setActiveConstructionInfo(null)}
            >
              <IconInfo width={18} height={18} color='currentColor' />
            </button>
            {activeConstructionInfo === 'work_duration' && (
              <div
                className={`${styles.fieldInfoTooltip} ${styles.fieldInfoTooltipWide}`}
                role='tooltip'
              >
                {t('calculator.forms.work_duration_info')}
              </div>
            )}
          </div>
        </div>
        <Select
          options={workDurationOptions}
          value={formData.work_duration_years || ''}
          onChange={value => {
            setFormData(prev => {
              const next = { ...prev, work_duration_years: value };
              if (prev.startDate && value) {
                next.endDate = getCarPolicyEndDateYmd(prev.startDate, value);
              }
              return next;
            });
          }}
          placeholder={t('calculator.forms.work_duration_placeholder')}
          className={
            validationErrors.work_duration_years ? styles.selectError : ''
          }
        />
        <div
          key={`car-work-duration-${(validationErrors.work_duration_years || '').length}`}
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.work_duration_years ? styles.errorHighlight : ''}`}
        >
          {validationErrors.work_duration_years || ''}
        </div>
      </div>

      {!hidePolicyholder ? (
      <>
      {/* Policyholder Address (optional) */}
      <div
        className={styles.formGroup}
        style={{ width: '100%', maxWidth: '100%', marginTop: '20px' }}
      >
        <Checkbox
          checked={formData.include_policyholder_address === true}
          onChange={checked => {
            setFormData(prev => ({
              ...prev,
              include_policyholder_address: checked,
              policyholder: {
                ...prev.policyholder,
                address: checked ? prev.policyholder?.address || {} : {},
              },
            }));
            if (!checked && setValidationErrors) {
              setValidationErrors(prev => {
                const next = { ...prev };
                delete next.policyholder_city;
                delete next.policyholder_street;
                delete next.policyholder_house_number;
                delete next.policyholder_apartment_number;
                delete next.policyholder_settlement_code;
                delete next.policyholder_street_code;
                delete next.policyholder_building_code;
                delete next.policyholder_room_code;
                return next;
              });
            }
          }}
          label={t('calculator.forms.policyholder_address_optional_checkbox')}
        />
      </div>

      {formData.include_policyholder_address && (
        <>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('calculator.forms.city')}</label>
            <AddressSearchInput
              value={formData.policyholder?.address?.settlement_code || ''}
              displayValue={formData.policyholder?.address?.city || ''}
              onChange={settlementCode => {
                handlePolicyholderAddressChange(
                  'settlement_code',
                  settlementCode
                );
              }}
              onSettlementSelect={settlement => {
                handlePolicyholderAddressChange(
                  'settlement_code',
                  settlement.code
                );
                handlePolicyholderAddressChange('city', settlement.name);
              }}
              placeholder={t('calculator.forms.city_placeholder')}
              error={!!validationErrors.policyholder_city}
            />
            <div
              className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_city ? styles.errorHighlight : ''}`}
            >
              {validationErrors.policyholder_city || ''}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>{t('calculator.forms.street')}</label>
            <StreetSearchInput
              value={formData.policyholder?.address?.street_code || ''}
              displayValue={formData.policyholder?.address?.street || ''}
              settlementCode={
                formData.policyholder?.address?.settlement_code || null
              }
              onChange={streetCode => {
                handlePolicyholderAddressChange('street_code', streetCode);
              }}
              onQueryChange={text => {
                handlePolicyholderAddressChange('street', text);
                handlePolicyholderAddressChange('street_code', '');
              }}
              onUnmatched={() => {
                if (!setValidationErrors) return;
                setValidationErrors(prev => ({
                  ...prev,
                  policyholder_street: t(
                    'calculator.forms.validation.street_select_from_list'
                  ),
                }));
              }}
              onStreetSelect={street => {
                handlePolicyholderAddressChange('street_code', street.code);
                handlePolicyholderAddressChange('street', street.name);
              }}
              placeholder={t('calculator.forms.street_placeholder')}
              error={!!validationErrors.policyholder_street}
            />
            <div
              className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_street ? styles.errorHighlight : ''}`}
            >
              {validationErrors.policyholder_street || ''}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t('calculator.forms.house_number')}
            </label>
            <BuildingSearchInput
              value={formData.policyholder?.address?.building_code || ''}
              displayValue={formData.policyholder?.address?.house_number || ''}
              streetCode={formData.policyholder?.address?.street_code || null}
              onChange={buildingCode => {
                handlePolicyholderAddressChange('building_code', buildingCode);
              }}
              onBuildingSelect={building => {
                handlePolicyholderAddressChange('building_code', building.code);
                handlePolicyholderAddressChange(
                  'house_number',
                  building.full_number || building.building_number
                );
              }}
              placeholder={t('calculator.forms.house_placeholder')}
              error={!!validationErrors.policyholder_house_number}
            />
            <div
              className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_house_number ? styles.errorHighlight : ''}`}
            >
              {validationErrors.policyholder_house_number || ''}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t('calculator.forms.apartment_number')}
            </label>
            <RoomSearchInput
              value={formData.policyholder?.address?.room_code || ''}
              displayValue={
                formData.policyholder?.address?.apartment_number || ''
              }
              buildingCode={formData.policyholder?.address?.building_code || null}
              onChange={roomCode => {
                handlePolicyholderAddressChange('room_code', roomCode);
              }}
              onRoomSelect={room => {
                handlePolicyholderAddressChange('room_code', room.code);
                handlePolicyholderAddressChange(
                  'apartment_number',
                  room.room_number
                );
              }}
              placeholder={t('calculator.forms.apartment_placeholder')}
              error={!!validationErrors.policyholder_apartment_number}
            />
            <div
              className={`${styles.fieldError} ${showValidationErrors && validationErrors.policyholder_apartment_number ? styles.errorHighlight : ''}`}
            >
              {validationErrors.policyholder_apartment_number || ''}
            </div>
          </div>
        </>
      )}
      </>
      ) : null}

      {/* Construction Address */}
      <div
        className={styles.formGroup}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <label
          className={styles.label}
          style={{
            fontSize: '18px',
            fontWeight: '500',
            marginBottom: '20px',
            marginTop: '20px',
          }}
        >
          {t('calculator.forms.construction_address')}
        </label>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.city')}</label>
        <AddressSearchInput
          value={formData.construction_address?.settlement_code || ''}
          displayValue={formData.construction_address?.city || ''}
          onChange={settlementCode => {
            handleConstructionAddressChange('settlement_code', settlementCode);
          }}
          onSettlementSelect={handleConstructionSettlementSelect}
          placeholder={t('calculator.forms.city_placeholder')}
          error={!!validationErrors.construction_city}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.construction_city ? styles.errorHighlight : ''}`}
        >
          {validationErrors.construction_city || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.street')}</label>
        <input
          className={`${styles.input} ${validationErrors.construction_street ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.street_construction_placeholder')}
          value={formData.construction_address?.street || ''}
          autoComplete='street-address'
          onChange={e => {
            handleConstructionAddressPatch({
              street: e.target.value,
              street_code: null,
              building_code: null,
              room_code: null,
            });
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.construction_street ? styles.errorHighlight : ''}`}
        >
          {validationErrors.construction_street || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.house_number')}</label>
        <input
          className={`${styles.input} ${validationErrors.construction_house_number ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.house_placeholder')}
          value={formData.construction_address?.house_number || ''}
          onChange={e => {
            handleConstructionAddressPatch({
              house_number: e.target.value,
              building_code: null,
              room_code: null,
            });
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.construction_house_number ? styles.errorHighlight : ''}`}
        >
          {validationErrors.construction_house_number || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.apartment_number')}
        </label>
        <input
          className={`${styles.input} ${validationErrors.construction_apartment_number ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.apartment_if_applicable')}
          value={formData.construction_address?.apartment_number || ''}
          onChange={e => {
            handleConstructionAddressPatch({
              apartment_number: e.target.value,
              room_code: null,
            });
          }}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.construction_apartment_number ? styles.errorHighlight : ''}`}
        >
          {validationErrors.construction_apartment_number || ''}
        </div>
      </div>

      {/* Building Type */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.field_building_type')}
        </label>
        <Select
          options={constructionCarBuildingOpts}
          value={formData.building_type || ''}
          onChange={handleBuildingTypeChange}
          placeholder={t('calculator.forms.building_type_placeholder')}
          className={validationErrors.building_type ? styles.selectError : ''}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.building_type ? styles.errorHighlight : ''}`}
        >
          {validationErrors.building_type || ''}
        </div>
      </div>

      {/* Construction material */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.field_construction_material')}
        </label>
        <Select
          options={constructionMaterialOpts}
          value={formData.construction_material || ''}
          onChange={
            handleConstructionMaterialChange ||
            (value => {
              setFormData(prev => ({
                ...prev,
                construction_material: value,
                ...(value !== 'OTHER'
                  ? { construction_material_other: '' }
                  : {}),
              }));
              if (setValidationErrors) {
                setValidationErrors(prev => {
                  if (
                    !prev?.construction_material &&
                    !prev?.construction_material_other
                  ) {
                    return prev;
                  }
                  const next = { ...prev };
                  if (value) delete next.construction_material;
                  if (value !== 'OTHER') delete next.construction_material_other;
                  return next;
                });
              }
            })
          }
          placeholder={t('calculator.forms.construction_material_placeholder')}
          className={
            validationErrors.construction_material ? styles.selectError : ''
          }
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.construction_material ? styles.errorHighlight : ''}`}
        >
          {validationErrors.construction_material || ''}
        </div>
      </div>

      {formData.construction_material === 'OTHER' && (
        <div className={styles.formGroup}>
          <label className={styles.label}>
            {t('calculator.forms.field_construction_material_other')}
          </label>
          <input
            className={`${styles.input} ${validationErrors.construction_material_other ? styles.inputError : ''}`}
            type='text'
            placeholder={t(
              'calculator.forms.construction_material_other_placeholder'
            )}
            value={formData.construction_material_other || ''}
            maxLength={255}
            onChange={e => {
              const value = e.target.value.slice(0, 255);
              setFormData(prev => ({
                ...prev,
                construction_material_other: value,
              }));
              if (value.trim() && setValidationErrors) {
                setValidationErrors(prev => {
                  if (!prev?.construction_material_other) return prev;
                  const next = { ...prev };
                  delete next.construction_material_other;
                  return next;
                });
              }
            }}
          />
          <div
            className={`${styles.fieldError} ${showValidationErrors && validationErrors.construction_material_other ? styles.errorHighlight : ''}`}
          >
            {validationErrors.construction_material_other || ''}
          </div>
        </div>
      )}

      {/* Work Value */}
      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.work_value_eur')}</label>
        <input
          className={`${styles.input} ${validationErrors.work_value ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.work_value_placeholder')}
          value={formData.work_value || ''}
          onChange={handleWorkValueChange}
          maxLength={8}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.work_value ? styles.errorHighlight : ''}`}
        >
          {validationErrors.work_value || ''}
        </div>
      </div>

      {/* Buildings Count */}
      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.buildings_count')}</label>
        <input
          className={`${styles.input} ${validationErrors.buildings_count ? styles.inputError : ''}`}
          type='text'
          placeholder={t('calculator.forms.buildings_count_placeholder')}
          value={formData.buildings_count || '1'}
          onChange={handleBuildingsCountChange}
          maxLength={1}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.buildings_count ? styles.errorHighlight : ''}`}
        >
          {validationErrors.buildings_count || ''}
        </div>
      </div>

      {/* Boolean Fields */}
      <div className={styles.formGroup}>
        <div className={styles.labelWithInfo}>
          <span
            id='common-walls-question'
            className={styles.label}
          >
            {t('calculator.forms.has_common_walls')}
          </span>
          <div
            className={styles.fieldInfoIconWrap}
            onMouseEnter={() => setActiveConstructionInfo('common_walls')}
            onMouseLeave={() => setActiveConstructionInfo(null)}
          >
            <button
              type='button'
              className={styles.packageInfoButton}
              aria-label={t('calculator.forms.has_common_walls_info')}
              onFocus={() => setActiveConstructionInfo('common_walls')}
              onBlur={() => setActiveConstructionInfo(null)}
            >
              <IconInfo width={18} height={18} color='currentColor' />
            </button>
            {activeConstructionInfo === 'common_walls' && (
              <div className={styles.fieldInfoTooltip} role='tooltip'>
                {t('calculator.forms.has_common_walls_info')}
              </div>
            )}
          </div>
        </div>
        <div
          className={styles.radioGroup}
          role='radiogroup'
          aria-labelledby='common-walls-question'
        >
          {yesNoOptions.map(opt => (
            <label
              key={String(opt.value)}
              className={`${styles.radioOption} ${formData.has_common_walls === opt.value ? styles.radioSelected : ''}`}
            >
              <input
                type='radio'
                name='has_common_walls'
                value={String(opt.value)}
                checked={formData.has_common_walls === opt.value}
                onChange={() =>
                  handleBooleanChange('has_common_walls', opt.value)
                }
                className={styles.radioInput}
              />
              <span className={styles.radioLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.formGroup}>
        <div className={styles.labelWithInfo}>
          <span id='heritage-area-question' className={styles.label}>
            {t('calculator.forms.heritage_area')}
          </span>
          <div
            className={styles.fieldInfoIconWrap}
            onMouseEnter={() => setActiveConstructionInfo('heritage')}
            onMouseLeave={() => setActiveConstructionInfo(null)}
          >
            <button
              type='button'
              className={styles.packageInfoButton}
              aria-label={t('calculator.forms.heritage_area_info')}
              onFocus={() => setActiveConstructionInfo('heritage')}
              onBlur={() => setActiveConstructionInfo(null)}
            >
              <IconInfo width={18} height={18} color='currentColor' />
            </button>
            {activeConstructionInfo === 'heritage' && (
              <div className={styles.fieldInfoTooltip} role='tooltip'>
                {t('calculator.forms.heritage_area_info')}
              </div>
            )}
          </div>
        </div>
        <div
          className={styles.radioGroup}
          role='radiogroup'
          aria-labelledby='heritage-area-question'
        >
          {yesNoOptions.map(opt => (
            <label
              key={String(opt.value)}
              className={`${styles.radioOption} ${formData.is_heritage_area === opt.value ? styles.radioSelected : ''}`}
            >
              <input
                type='radio'
                name='is_heritage_area'
                value={String(opt.value)}
                checked={formData.is_heritage_area === opt.value}
                onChange={() =>
                  handleBooleanChange('is_heritage_area', opt.value)
                }
                className={styles.radioInput}
              />
              <span className={styles.radioLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.formGroup}>
        <div className={styles.labelWithInfo}>
          <span id='includes-demolition-question' className={styles.label}>
            {t('calculator.forms.includes_demolition')}
          </span>
          <div
            className={styles.fieldInfoIconWrap}
            onMouseEnter={() => setActiveConstructionInfo('demolition')}
            onMouseLeave={() => setActiveConstructionInfo(null)}
          >
            <button
              type='button'
              className={styles.packageInfoButton}
              aria-label={t('calculator.forms.includes_demolition_info')}
              onFocus={() => setActiveConstructionInfo('demolition')}
              onBlur={() => setActiveConstructionInfo(null)}
            >
              <IconInfo width={18} height={18} color='currentColor' />
            </button>
            {activeConstructionInfo === 'demolition' && (
              <div className={styles.fieldInfoTooltip} role='tooltip'>
                {t('calculator.forms.includes_demolition_info')}
              </div>
            )}
          </div>
        </div>
        <div
          className={styles.radioGroup}
          role='radiogroup'
          aria-labelledby='includes-demolition-question'
        >
          {yesNoOptions.map(opt => (
            <label
              key={String(opt.value)}
              className={`${styles.radioOption} ${formData.includes_demolition === opt.value ? styles.radioSelected : ''}`}
            >
              <input
                type='radio'
                name='includes_demolition'
                value={String(opt.value)}
                checked={formData.includes_demolition === opt.value}
                onChange={() =>
                  handleBooleanChange('includes_demolition', opt.value)
                }
                className={styles.radioInput}
              />
              <span className={styles.radioLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className={styles.formGroup}>
        <div className={styles.labelWithInfo}>
          <span id='prior-claims-question' className={styles.label}>
            {t('calculator.forms.has_prior_claims')}
          </span>
          <div
            className={styles.fieldInfoIconWrap}
            onMouseEnter={() => setActiveConstructionInfo('prior_claims')}
            onMouseLeave={() => setActiveConstructionInfo(null)}
          >
            <button
              type='button'
              className={styles.packageInfoButton}
              aria-label={t('calculator.forms.has_prior_claims_info')}
              onFocus={() => setActiveConstructionInfo('prior_claims')}
              onBlur={() => setActiveConstructionInfo(null)}
            >
              <IconInfo width={18} height={18} color='currentColor' />
            </button>
            {activeConstructionInfo === 'prior_claims' && (
              <div className={styles.fieldInfoTooltip} role='tooltip'>
                {t('calculator.forms.has_prior_claims_info')}
              </div>
            )}
          </div>
        </div>
        <div
          className={styles.radioGroup}
          role='radiogroup'
          aria-labelledby='prior-claims-question'
        >
          {yesNoOptions.map(opt => (
            <label
              key={String(opt.value)}
              className={`${styles.radioOption} ${formData.has_prior_claims === opt.value ? styles.radioSelected : ''}`}
            >
              <input
                type='radio'
                name='has_prior_claims'
                value={String(opt.value)}
                checked={formData.has_prior_claims === opt.value}
                onChange={() =>
                  handleBooleanChange('has_prior_claims', opt.value)
                }
                className={styles.radioInput}
              />
              <span className={styles.radioLabel}>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* File Upload */}
      <div
        className={styles.formGroup}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <label
          className={styles.label}
          style={{
            fontSize: '18px',
            fontWeight: '500',
            marginBottom: '20px',
            marginTop: '20px',
          }}
        >
          {t('calculator.forms.documents')}
        </label>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>
          {t('calculator.forms.construction_permit_pdf')}
        </label>
        <FileInput
          accept='.pdf'
          value={formData.statybos_leidimas || []}
          chooseLabel={t('calculator.forms.file_input_choose')}
          emptyLabel={t('calculator.forms.file_input_empty')}
          hasError={
            showValidationErrors && !!validationErrors.statybos_leidimas
          }
          onChange={files => handleFileChange('statybos_leidimas', files)}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.statybos_leidimas ? styles.errorHighlight : ''}`}
        >
          {validationErrors.statybos_leidimas || ''}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>{t('calculator.forms.estimate_pdf')}</label>
        <FileInput
          accept='.pdf'
          value={formData.samata || []}
          chooseLabel={t('calculator.forms.file_input_choose')}
          emptyLabel={t('calculator.forms.file_input_empty')}
          hasError={showValidationErrors && !!validationErrors.samata}
          onChange={files => handleFileChange('samata', files)}
        />
        <div
          className={`${styles.fieldError} ${showValidationErrors && validationErrors.samata ? styles.errorHighlight : ''}`}
        >
          {validationErrors.samata || ''}
        </div>
      </div>
    </>
  );
}
